// Two-layer, dependency-free browser fingerprint for multi-account /
// self-referral fraud review. Never an identity source by itself — always a
// probabilistic signal for admin review. Each layer degrades independently
// (one failing never blocks the other) and the whole thing is optional:
// missing fingerprints just mean less signal, never a broken signup/login.
//
//   primary   (device-bound)  — WebGL: GPU vendor/renderer/extensions + a
//                                pixel-readback hash, combined with screen/
//                                timezone/language. Designed to survive a
//                                browser switch on the SAME physical device
//                                (Chrome -> Brave, etc.) since it reads the
//                                actual GPU rather than browser-spoofable
//                                hardware fields.
//   secondary (browser-bound) — the original recipe: canvas + full hardware
//                                (platform/cores/memory/touch) + language.
//                                Best same-browser signal; not reliable
//                                cross-browser.
//
// There used to be a third "audio" layer (OfflineAudioContext oscillator
// signature). It was removed: audio-context fingerprinting is a pure
// software DSP computation, not real hardware readback, and on many
// OS+browser combinations (Android+Chrome in particular) it produces
// bit-identical output across completely unrelated physical devices — it
// was linking unrelated strangers' accounts together as false positives.
//
// Privacy browsers (Brave, Firefox strict mode, Safari ITP) deliberately
// randomize canvas/WebGL readback for some users — that's expected and
// accepted; those layers fall back to their non-randomized components only
// (GPU vendor/renderer, hardware fields) rather than failing outright.

const FP_CACHE_KEY = 'ls_fp4';

// ---------- canvas (secondary layer) ----------

const drawCanvas = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 220;
  canvas.height = 40;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(60, 4, 100, 24);
  ctx.fillStyle = '#069';
  ctx.fillText('LunaSearch <fp> 1.0', 2, 8);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('LunaSearch <fp> 1.0', 4, 18);
  ctx.strokeStyle = 'rgb(120, 30, 200)';
  ctx.beginPath();
  ctx.arc(30, 20, 15, 0, Math.PI * 1.5);
  ctx.stroke();
  return canvas.toDataURL();
};

// Rendered twice and compared — if readback is randomized (Safari 16.4+,
// Brave), the signature is dropped so the hash stays stable per browser.
const canvasSignature = () => {
  try {
    const first = drawCanvas();
    const second = drawCanvas();
    if (!first || first !== second) return { signature: '', stable: false };
    return { signature: first, stable: true };
  } catch {
    return { signature: '', stable: false };
  }
};

// ---------- WebGL (primary layer) ----------

const VERTEX_SHADER = `
  attribute vec2 position;
  attribute vec3 color;
  varying vec3 vColor;
  void main() {
    vColor = color;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;
const FRAGMENT_SHADER = `
  precision mediump float;
  varying vec3 vColor;
  void main() {
    gl_FragColor = vec4(vColor, 1.0);
  }
`;

const renderWebglPixels = (gl, size) => {
  gl.viewport(0, 0, size, size);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);

  const compile = (type, src) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    return shader;
  };
  const program = gl.createProgram();
  gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX_SHADER));
  gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
  gl.linkProgram(program);
  gl.useProgram(program);

  // one shaded triangle — position(x,y) + color(r,g,b) per vertex
  const vertices = new Float32Array([
    -0.9, -0.9, 1, 0, 0,
    0.9, -0.9, 0, 1, 0,
    0.0, 0.9, 0, 0, 1,
  ]);
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

  const posLoc = gl.getAttribLocation(program, 'position');
  const colorLoc = gl.getAttribLocation(program, 'color');
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 20, 0);
  gl.enableVertexAttribArray(colorLoc);
  gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 20, 8);

  gl.drawArrays(gl.TRIANGLES, 0, 3);

  const pixels = new Uint8Array(size * size * 4);
  gl.readPixels(0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  // sample rather than stringify the whole buffer
  let sample = '';
  for (let i = 0; i < pixels.length; i += 977) sample += pixels[i];
  return sample;
};

const webglSignature = () => {
  try {
    const size = 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return null;

    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = String((ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR)) || '');
    const renderer = String((ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)) || '');
    const extCount = (gl.getSupportedExtensions() || []).length;

    let first = '';
    let second = '';
    try {
      first = renderWebglPixels(gl, size);
      second = renderWebglPixels(gl, size);
    } catch {
      // drawing failed — vendor/renderer/extensions are still usable
    }
    const pixelStable = Boolean(first) && first === second;

    return { vendor, renderer, extCount, pixelSample: pixelStable ? first : '', pixelStable };
  } catch {
    return null;
  }
};

// ---------- shared ----------

const sha256Hex = async (text) => {
  const data = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Returns { primary, secondary }, each either
 * { hash, components } or null if that layer couldn't be computed at all.
 * Cached in localStorage; never throws.
 */
export const getFingerprint = async () => {
  try {
    try {
      const cached = JSON.parse(localStorage.getItem(FP_CACHE_KEY) || 'null');
      if (cached && (cached.primary || cached.secondary)) return cached;
    } catch {}

    if (!window.crypto || !window.crypto.subtle) return { primary: null, secondary: null };

    const screenSig = window.screen
      ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
      : '';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const lang = navigator.language || '';

    const canvas = canvasSignature();
    const webgl = webglSignature();

    let primary = null;
    if (webgl) {
      const parts = [
        webgl.pixelStable ? webgl.pixelSample : 'webgl-randomized',
        webgl.vendor,
        webgl.renderer,
        String(webgl.extCount),
        screenSig,
        tz,
        lang,
      ];
      primary = {
        hash: await sha256Hex(parts.join('||')),
        components: {
          screen: screenSig,
          tz,
          lang,
          webglVendor: webgl.vendor.slice(0, 120),
          webglRenderer: webgl.renderer.slice(0, 120),
          webglExtCount: webgl.extCount,
          pixelStable: webgl.pixelStable,
        },
      };
    }

    const platform = navigator.platform || '';
    const cores = navigator.hardwareConcurrency || 0;
    const memory = navigator.deviceMemory || 0;
    const touchPoints = navigator.maxTouchPoints || 0;
    const secondaryParts = [
      canvas.stable ? canvas.signature : 'canvas-randomized',
      webgl ? webgl.renderer : '',
      platform,
      String(cores),
      String(memory),
      String(touchPoints),
      lang,
    ];
    const secondary = {
      hash: await sha256Hex(secondaryParts.join('||')),
      components: {
        canvasStable: canvas.stable,
        platform,
        cores,
        memory,
        touchPoints,
        gpu: webgl ? webgl.renderer.slice(0, 120) : '',
        lang,
      },
    };

    const result = { primary, secondary };

    try {
      localStorage.setItem(FP_CACHE_KEY, JSON.stringify(result));
      localStorage.removeItem('ls_fp3'); // old 3-layer format (had audio)
      localStorage.removeItem('ls_fp2');
      localStorage.removeItem('ls_fp');
    } catch {
      // storage full/blocked — fingerprint still works, just uncached
    }
    return result;
  } catch {
    return { primary: null, secondary: null };
  }
};
